import * as React from 'react';
import { Stack } from 'react-bootstrap';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getConfigOrThrow } from 'gossamer-front-end';
import HospitalsHero from './HospitalsHero';
import ProvidersHero from './ProvidersHero';
import UniversitiesHero from './UniversitiesHero';
import {
  externalPaths,
  getMarketingFeature,
  marketingFeatures,
  marketingPaths,
  MarketingFeature,
} from './marketingData';
import {
  FeatureLinks,
  MarketingHero,
  MarketingLink,
  MarketingPage,
  MarketingSection,
  Placeholder,
  marketingStyles as styles,
} from './MarketingShell';
import { LogoBar } from 'shared-front-end';

const ProofAvatars = () => (
  <span className={styles.Avatars} aria-hidden="true">
    <span className={styles.Avatar}>NHS</span>
    <span className={styles.Avatar}>UNI</span>
    <span className={styles.Avatar}>CPD</span>
  </span>
);

const CtaBand = ({
  title,
  body,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
}: {
  title: string;
  body: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel?: string;
  secondaryHref?: string;
}) => (
  <div className={styles.CtaBand}>
    <h2>{title}</h2>
    <p>{body}</p>
    <div className={styles.Actions}>
      <MarketingLink href={primaryHref}>{primaryLabel}</MarketingLink>
      {secondaryLabel && secondaryHref && (
        <MarketingLink href={secondaryHref} secondary>
          {secondaryLabel}
        </MarketingLink>
      )}
    </div>
  </div>
);

export const MarketingLandingPage = () => (
  <MarketingPage>
    <MarketingHero
      eyebrow="For hospitals, universities, societies and course providers"
      title="Medical education that runs itself."
      summary="Careerbook automates timetabling, attendance, feedback and compliance reporting for undergraduate and postgraduate teaching and public live webinars and courses — saving teams 90% of admin workload."
      actions={
        <>
          <MarketingLink href={externalPaths.createEvent}>Create an event</MarketingLink>
          <MarketingLink href={marketingPaths.contact} secondary>
            Get a demo
          </MarketingLink>
        </>
      }
      proof={
        <>
          <ProofAvatars />
          <span>
            Trusted by <strong>270+ healthcare organisations</strong> and{' '}
            <strong>175,000+ healthcare professionals</strong>
          </span>
        </>
      }
      visual={
        <a
          className={styles.Video}
          href="https://youtu.be/Bobyod4qYGg"
          target="_blank"
          rel="noreferrer"
          aria-label="Watch the Careerbook platform demo on YouTube"
        >
          <div className={styles.VideoContent}>
            <span className={styles.Play} aria-hidden="true">
              ▶
            </span>
            <strong>Careerbook demo</strong>
            <div>See the platform in action</div>
          </div>
        </a>
      }
    />
  </MarketingPage>
);

const PlatformTabs = () => {
  const [activeSlug, setActiveSlug] = React.useState(marketingFeatures[0].slug);
  const feature = getMarketingFeature(activeSlug) || marketingFeatures[0];

  return (
    <>
      <div className={styles.Tabs} role="tablist" aria-label="Careerbook platform features">
        {marketingFeatures.map((item) => (
          <button
            key={item.slug}
            type="button"
            role="tab"
            aria-selected={item.slug === activeSlug}
            className={`${styles.Tab} ${item.slug === activeSlug ? styles.TabActive : ''}`}
            onClick={() => setActiveSlug(item.slug)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className={styles.FeaturePanel} role="tabpanel">
        <div>
          <h3>{feature.title}</h3>
          <p>{feature.summary}</p>
          <ul>
            {feature.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
          <div className={styles.Actions}>
            <MarketingLink href={marketingPaths.feature(feature.slug)}>Learn more</MarketingLink>
            <MarketingLink href={marketingPaths.contact} secondary>
              See it in a demo
            </MarketingLink>
          </div>
        </div>
        <SampleTable feature={feature} />
      </div>
    </>
  );
};

const SampleTable = ({ feature }: { feature: MarketingFeature }) => (
  <div className={styles.Sample}>
    <h3>{feature.sampleTitle}</h3>
    <div className={styles.TableWrap}>
      <table className={styles.Table}>
        <tbody>
          {feature.sampleRows.map((row) => (
            <tr key={row.join('-')}>
              {row.map((cell) => (
                <td key={cell}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <small>Illustrative data</small>
  </div>
);

const trustFaqs = [
  [
    'How long does implementation take?',
    'Most Trusts go live in around four weeks: setup and data collection, upload and configuration, admin training, then go-live with a named customer success manager.',
  ],
  [
    'Does it cover undergraduate and postgraduate teaching?',
    'Yes. Careerbook supports medical student placements alongside Foundation, core and specialty teaching, simulation, inductions and mandatory training.',
  ],
  [
    'How is attendance verified?',
    'Learners use a session QR code, an e-signature or a one-time location check. Every record shows which method was used.',
  ],
  [
    'Can we import existing timetables and learner lists?',
    'Yes. Timetables, groups and learner emails can be bulk-uploaded by CSV during onboarding.',
  ],
  [
    'How is pricing structured?',
    'Pricing uses an annual per-user enterprise licence, tiered by user count, with term discounts and a one-time onboarding fee.',
  ],
  [
    'Where is our data stored and who can access it?',
    'Data is hosted in the UK on ISO 27001-certified infrastructure, encrypted in transit and at rest, with role-based permissions.',
  ],
  ['Do learners need to install an app?', 'No. Careerbook works in a browser on mobile, tablet and desktop.'],
  [
    'What support do we get after go-live?',
    'Live and on-demand training, customer support, monthly reviews and video guides for admins and learners.',
  ],
];

export const HospitalsAndTrusts = () => (
  <MarketingPage>
    <MarketingHero
      eyebrow={
        <>
          <img src="/assets/icons/for-nhs-hospitals-and-trusts.svg" alt="" /> For NHS hospitals and medical education
          teams
        </>
      }
      title="Medical education that runs itself."
      summary="Careerbook automates timetabling, attendance, feedback and compliance reporting for undergraduate and postgraduate teaching — giving your team back more than 90% of its admin time."
      actions={
        <>
          <MarketingLink href={marketingPaths.contact}>Book a demo</MarketingLink>
          <MarketingLink href="#platform" secondary>
            See the platform
          </MarketingLink>
        </>
      }
      proof={
        <Stack direction="horizontal" gap={2}>
          <img src="/assets/icons/trust-circles.svg" />
          <span>
            Trusted by <b>270+ healthcare organisations</b> and <b>175,000+ clinicians and learners</b>
          </span>
        </Stack>
      }
      visual={<HospitalsHero registerCount="30 of 32 signed in" registerPct={94} />}
    />

    <LogoBar />

    <MarketingSection
      title="7–10 minutes of admin per learner, per session. We take it to one."
      intro="We audited how hospitals run teaching today. Scheduling, sign-ins, chasing feedback, making certificates and producing reports quietly consume whole posts. Careerbook replaces every one of those steps with a workflow that runs on its own."
      narrow
    >
      <div className={styles.TableWrap}>
        <table className={styles.Table}>
          <thead>
            <tr>
              <th>Where the time goes</th>
              <th>Today</th>
              <th>With Careerbook</th>
            </tr>
          </thead>
          <tbody>
            {[
              [
                'Timetabling',
                '2–4 weeks per block or rotation. Spreadsheets, back-and-forth emails, clashes found late.',
                '1–2 days. AI builds the timetable, flags clashes and publishes it to every learner’s calendar.',
              ],
              [
                'Attendance',
                'Paper sign-in sheets, chasing signatures, no proof of who was actually there.',
                'QR code, e-signature or a one-time location check at sign-in. Every record shows how it was captured.',
              ],
              [
                'Feedback and certificates',
                'Chasing forms for weeks, then making certificates one by one.',
                'Forms go out after every session. Certificates issue themselves once feedback is in.',
              ],
              [
                'Learner reminders and follow-up',
                'Email chains and WhatsApp groups. Reminders depend on someone remembering.',
                'Automatic reminders to learners. Instant lateness and absence alerts to educators.',
              ],
              [
                'Reporting and audit preparation',
                'Days compiling evidence for GMC, NHS England and internal audits.',
                'One-click compliance reports by department, cohort or individual.',
              ],
            ].map((activity) => (
              <tr>
                <td>{activity[0]}</td>
                <td>{activity[1]}</td>
                <td>{activity[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MarketingSection>

    <MarketingSection
      id="platform"
      title="One platform for every teaching session in your Trust"
      intro="From the timetable to the audit report, each step hands off to the next automatically."
    >
      <p>
        <Link to={externalPaths.videoGuides}>Watch the video guides →</Link>
      </p>
      <PlatformTabs />
    </MarketingSection>

    <MarketingSection
      title="The numbers behind the hours"
      intro="Careerbook saves an average large NHS Trust around 14,000 hours and £280,000 a year — roughly 5–8 full-time posts of admin."
    >
      <div className={styles.Grid4}>
        {[
          ['175,000+', 'clinicians and learners supported'],
          ['270+', 'healthcare organisations'],
          ['30,000+', 'teaching sessions created'],
          ['90%', 'less admin time per session'],
        ].map(([value, label]) => (
          <div className={styles.Card} key={value}>
            <h3>{value}</h3>
            <p>{label}</p>
          </div>
        ))}
      </div>
    </MarketingSection>

    <MarketingSection
      title="Live in about four weeks"
      intro="A named customer success manager takes your team from spreadsheets to go-live, with live and on-demand training for admins and learners."
    >
      <div className={styles.Grid4}>
        {[
          [
            'Prepare (Week 1)',
            'Set up your Trust account and branding, administrators, learner lists, groups and timetables.',
          ],
          [
            'Configure (Weeks 1 - 3)',
            'Bulk-upload data by CSV and review groups, cohorts and timetables with your team.',
          ],
          ['Train (Week 3)', 'Run two 90-minute Teams sessions for medical education managers and teaching staff.'],
          ['Go live (Week 4)', 'Onboard learners, run teaching and continue with monthly reviews and support.'],
        ].map(([title, body], index) => (
          <div className={styles.Card} key={title}>
            <span className={styles.StepNumber}>{index + 1}</span>
            <h3>{title}</h3>
            <p>{body}</p>
          </div>
        ))}
      </div>
      <div className={styles.Actions}>
        <MarketingLink href={marketingPaths.contact}>Plan your rollout</MarketingLink>
      </div>
    </MarketingSection>

    <MarketingSection title="What medical education teams say">
      <div className={styles.Grid3}>
        {[
          {
            quote:
              'Whenever we’ve asked for a change, the Careerbook team has turned it around quickly. Updates arrive fast, and they reflect how our teaching actually runs.',
            attribution: 'Medical Education Manager',
            organisation: 'East Kent Hospitals University NHS Foundation Trust',
            icon: 'double-quotes-blue-1.svg',
          },
          {
            quote:
              'The customer support has been excellent. Every question from our team is answered promptly, and they’ve been with us at every step.',
            attribution: 'Medical Education Team',
            organisation: 'East Cheshire NHS Trust',
            icon: 'double-quotes-blue-2.svg',
          },
          {
            quote:
              'Careerbook showed us attendance we were missing and flagged learners with repeated absences — gaps we simply couldn’t see before.',
            attribution: 'Medical Education Team',
            organisation: 'Sheffield Teaching Hospitals NHS Foundation Trust',
            icon: 'double-quotes-orange.svg',
          },
        ].map((testimonial) => (
          <figure className={styles.TestimonialCard} key={testimonial.organisation}>
            <img
              className={styles.TestimonialQuoteIcon}
              src={`/assets/icons/${testimonial.icon}`}
              alt=""
              aria-hidden="true"
            />
            <blockquote>{testimonial.quote}</blockquote>
            <figcaption>
              <strong>{testimonial.attribution}</strong>
              <span>{testimonial.organisation}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </MarketingSection>

    <MarketingSection
      title="Built by doctors who ran the teaching themselves"
      intro="Careerbook is a UK team of NHS doctors and software engineers. We understand the problem from the ward to the codebase — which is why the platform is built around how medical education teams actually work."
      narrow
    />

    <MarketingSection
      title="Built for NHS information governance"
      intro="UK-hosted, encrypted and access-controlled. Everything your IG team needs is in one place."
    >
      <div className={styles.Actions}>
        <MarketingLink href={marketingPaths.trust}>Visit the Trust centre</MarketingLink>
      </div>
    </MarketingSection>

    <MarketingSection
      title="Questions from Trusts"
      intro="Can’t see yours? Our team usually replies within 24 hours."
      narrow
    >
      {trustFaqs.map(([question, answer]) => (
        <details className={styles.Faq} key={question}>
          <summary>{question}</summary>
          <p>{answer}</p>
        </details>
      ))}
      <div className={styles.Actions}>
        <MarketingLink href={marketingPaths.contact}>Ask us directly</MarketingLink>
      </div>
    </MarketingSection>

    <MarketingSection>
      <div className={`${styles.CtaBand} ${styles.CtaBandSplit}`}>
        <div>
          <span className={styles.CtaEyebrow}>August and February rotations start fast</span>
          <h2>Have your next rotation’s teaching run itself.</h2>
          <p>
            Go live in around four weeks. Book a walkthrough and we’ll show you your own timetable running on
            Careerbook.
          </p>
          <ul className={styles.CtaChecklist}>
            <li>Annual per-user licence, with savings on two- and five-year terms</li>
            <li>Onboarding, training and support included</li>
            <li>Information governance pack ready for your IG team</li>
          </ul>
          <div className={styles.Actions}>
            <MarketingLink href={marketingPaths.trust} secondary>
              Visit the Trust centre
            </MarketingLink>
          </div>
        </div>
        <div className={styles.CtaBandForm}>
          <h3>Book a demo</h3>
          <DemoRequestForm />
        </div>
      </div>
    </MarketingSection>
  </MarketingPage>
);

export const FeaturePage = () => {
  const { slug = '' } = useParams();
  const feature = getMarketingFeature(slug);
  if (!feature) return <Navigate to={marketingPaths.hospitals} replace />;

  return (
    <MarketingPage>
      <MarketingHero
        eyebrow={feature.label}
        title={feature.title}
        summary={feature.summary}
        actions={
          <>
            <MarketingLink href={marketingPaths.contact}>Book a demo</MarketingLink>
            <MarketingLink href={externalPaths.videoGuides} secondary>
              Watch the guide
            </MarketingLink>
          </>
        }
        visual={<SampleTable feature={feature} />}
      />
      <MarketingSection title="How it works">
        <div className={styles.Grid3}>
          {feature.bullets.map((bullet, index) => (
            <div className={styles.Card} key={bullet}>
              <span className={styles.StepNumber}>{index + 1}</span>
              <h3>{bullet.split('.')[0]}</h3>
              <p>{bullet}</p>
            </div>
          ))}
        </div>
      </MarketingSection>
      <MarketingSection title="Why teams use it">
        <div className={styles.Grid3}>
          {feature.benefits.map((benefit) => (
            <div className={styles.Card} key={benefit.title}>
              <h3>{benefit.title}</h3>
              <p>{benefit.body}</p>
            </div>
          ))}
        </div>
      </MarketingSection>
      <MarketingSection title="Good to know" narrow>
        {feature.faqs.map((faq) => (
          <details className={styles.Faq} key={faq.question}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
            {faq.unverified && (
              <Placeholder>This answer is visibly provisional and must be confirmed before launch.</Placeholder>
            )}
          </details>
        ))}
      </MarketingSection>
      <MarketingSection title="Works well with">
        <div className={styles.Grid3}>
          {feature.related.map((relatedSlug) => {
            const related = getMarketingFeature(relatedSlug);
            return related ? (
              <Link className={styles.Card} to={marketingPaths.feature(related.slug)} key={related.slug}>
                <h3>{related.label}</h3>
                <p>{related.title}</p>
              </Link>
            ) : null;
          })}
        </div>
      </MarketingSection>
      <MarketingSection>
        <CtaBand
          title={`See ${feature.label.toLowerCase()} with your own teaching`}
          body="Book a short walkthrough using your programmes and timetables."
          primaryLabel="Book a demo"
          primaryHref={marketingPaths.contact}
          secondaryLabel="All features"
          secondaryHref={`${marketingPaths.hospitals}#platform`}
        />
      </MarketingSection>
    </MarketingPage>
  );
};

export const UniversitiesAndSocieties = () => (
  <MarketingPage>
    <MarketingHero
      eyebrow={
        <>
          <img src="/assets/icons/for-universities-and-student-societies.svg" alt="" /> For universities and student
          societies
        </>
      }
      title="Run your teaching series. Skip the admin."
      summary="Host webinars, revision workshops and conferences on Careerbook. Attendance, certificates, feedback and payments run automatically — and your events reach 125,000+ students and doctors."
      actions={
        <>
          <MarketingLink href="#sponsorship">Apply for sponsorship</MarketingLink>
          <MarketingLink href={externalPaths.createEvent} secondary>
            Create an event
          </MarketingLink>
        </>
      }
      visual={<UniversitiesHero />}
    />
    <MarketingSection
      title="You don’t start from zero"
      intro="Your events are listed to an active audience already booking teaching."
      narrow
    />
    <MarketingSection
      title="Everything a committee needs to run great events"
      intro="Focus on the teaching. Careerbook handles everything around it."
    >
      <div className={styles.Grid3}>
        {[
          [
            'Host webinars directly',
            'Run online and hybrid teaching on Careerbook Live — no separate Zoom account needed.',
          ],
          ['Instant attendance', 'Every event gets a QR code. In-person sessions can use location check-in.'],
          ['Automatic certificates', 'Attendees receive certificates as soon as they complete feedback.'],
          ['Custom feedback forms', 'Build forms once, reuse them across your series and see summaries instantly.'],
          ['Payments in one click', 'Sell tickets, manage refunds and run paid events without spreadsheets.'],
          [
            'Portfolio points for students',
            'Show learners how attending and presenting builds their training portfolio.',
          ],
        ].map(([title, body]) => (
          <div className={styles.Card} key={title}>
            <span className={styles.IconBadge}>✓</span>
            <h3>{title}</h3>
            <p>{body}</p>
          </div>
        ))}
      </div>
    </MarketingSection>
    <MarketingSection
      id="sponsorship"
      title="Get funded for your events"
      intro="Societies that run events on Careerbook can earn grants of up to £500 a term, plus promotion through our social channels, newsletter, homepage and posters in hospitals and universities."
    >
      <div className={styles.Grid3}>
        {[
          [
            'Bronze',
            'Regular events, small-group teaching and webinars',
            'Pro plan · 3+ events · 50+ attendees · 1 society referral',
          ],
          [
            'Silver',
            'Teaching series, webinar programmes and revision workshops',
            'Pro plan · 5+ events · 200+ attendees · 3 society referrals',
          ],
          [
            'Gold',
            'Conferences and national or international events',
            'Premium plan · 3+ events or one conference · 500–750+ attendees · 3 referrals',
          ],
        ].map(([tier, audience, requirements]) => (
          <div className={styles.Card} key={tier}>
            <h3>{tier}</h3>
            <p>
              <strong>For:</strong> {audience}
            </p>
            <p>
              <strong>Requirements:</strong> {requirements}; Careerbook as main sponsor and social promotion.
            </p>
            <div className={styles.Actions}>
              <MarketingLink href={`${marketingPaths.contact}?type=Student%20society`}>Apply for {tier}</MarketingLink>
            </div>
          </div>
        ))}
      </div>
      <p>Terms apply. A short agreement confirms sponsorship, payment details and requirements before activation.</p>
    </MarketingSection>
    <MarketingSection>
      <CtaBand
        title="Planning this term’s teaching?"
        body="Set up your society in minutes and apply for sponsorship before your first event."
        primaryLabel="Apply for sponsorship"
        primaryHref={`${marketingPaths.contact}?type=Student%20society`}
        secondaryLabel="Create an event"
        secondaryHref={externalPaths.createEvent}
      />
    </MarketingSection>
  </MarketingPage>
);

export const CourseProviders = () => (
  <MarketingPage>
    <MarketingHero
      eyebrow={
        <>
          <img src="/assets/icons/for-course-and-cpd-providers.svg" alt="" /> For course and CPD providers
        </>
      }
      title="Fill your courses. We’ll handle the rest."
      summary="List your course on Careerbook and reach doctors and healthcare professionals actively looking for training. Ticketing, payments, attendance and certificates are all built in."
      actions={
        <>
          <MarketingLink href={externalPaths.createEvent}>List your course</MarketingLink>
          <MarketingLink href={marketingPaths.contact} secondary>
            Talk to our team
          </MarketingLink>
        </>
      }
      visual={<ProvidersHero />}
    />
    <MarketingSection
      title="From listing to certificate, in one place"
      intro="Between hosting, ticketing and chasing feedback, running courses is a second job — especially when you’re also practising clinically."
    >
      <div className={styles.Grid4}>
        {[
          ['List', 'Publish your course, conference or webinar in minutes, public or invite-only.'],
          ['Sell', 'Take payments and manage refunds from one central portal.'],
          ['Deliver', 'Run it in person with QR and location check-in, or online on Careerbook Live.'],
          ['Follow up', 'Send feedback forms and certificates automatically, then review the summary.'],
        ].map(([title, body], index) => (
          <div className={styles.Card} key={title}>
            <span className={styles.StepNumber}>{index + 1}</span>
            <h3>{title}</h3>
            <p>{body}</p>
          </div>
        ))}
      </div>
    </MarketingSection>
    <MarketingSection>
      <CtaBand
        title="Your next course, fully booked."
        body="List it today and let Careerbook do the admin."
        primaryLabel="List your course"
        primaryHref={externalPaths.createEvent}
        secondaryLabel="Talk to our team"
        secondaryHref={`${marketingPaths.contact}?type=Course%20or%20CPD%20provider`}
      />
    </MarketingSection>
  </MarketingPage>
);

const complianceRows = [
  ['UK GDPR and Data Protection Act 2018', 'Compliant', 'Privacy notice, data processing agreement', false],
  [
    'NHS Data Security and Protection Toolkit (DSPT)',
    'Aligned with principles',
    'Submission status, ODS code and date',
    true,
  ],
  ['ISO/IEC 27001', 'Hosting provider certified', 'Certificate holder and scope', true],
  ['Cyber Essentials / Cyber Essentials Plus', 'Compliant', 'Certificate number', true],
  ['NHS DTAC', 'Aligned with principles', 'Submission evidence', true],
  ['Privacy and Electronic Communications Regulations (PECR)', 'Compliant', 'Cookie consent controls', false],
] as const;

export const TrustCentre = () => (
  <MarketingPage>
    <MarketingHero
      eyebrow="Trust centre"
      title="Data governance, security and privacy"
      summary="NHS Trusts, universities and learners trust Careerbook with sensitive education and workforce data. This page sets out how we protect it, who processes it and how long we keep it."
      actions={
        <>
          <MarketingLink href={`${marketingPaths.contact}?subject=Security%20pack`}>
            Request the security pack
          </MarketingLink>
          <MarketingLink href={`${marketingPaths.contact}?subject=Data%20processing%20agreement`} secondary>
            Request a data processing agreement
          </MarketingLink>
        </>
      }
      proof={null}
    />
    <MarketingSection narrow>
      <nav className={styles.AnchorNav} aria-label="Trust centre sections">
        <a href="#security">Security architecture</a>
        <a href="#compliance">Compliance matrix</a>
        <a href="#principles">Data processing principles</a>
        <Link to={marketingPaths.contact}>Contact our team</Link>
      </nav>
    </MarketingSection>
    <MarketingSection
      id="security"
      title="Security architecture"
      intro="Defence in depth: every request passes through encrypted transport, authenticated access and role-based permissions before it reaches UK-hosted, encrypted storage."
    >
      <div className={styles.Grid4}>
        {[
          [
            'Learners, educators and admins',
            'Browser access on mobile, tablet or desktop. Two-factor authentication for admin accounts.',
          ],
          ['Encrypted transport', 'All traffic uses HTTPS with TLS 1.2 or higher.'],
          ['Locked API and role-based access', 'Permissions are scoped to admin, staff and learner roles.'],
          ['UK data centre', 'Application and databases are UK-hosted and encrypted at rest.'],
        ].map(([title, body], index) => (
          <div className={styles.Card} key={title}>
            <span className={styles.StepNumber}>{index + 1}</span>
            <h3>{title}</h3>
            <p>{body}</p>
          </div>
        ))}
      </div>
      <div className={styles.Grid4}>
        {[
          'UK-hosted on ISO 27001-certified servers',
          'TLS 1.2+ in transit, encrypted at rest',
          'UK GDPR and DPA 2018 compliant',
          'We never sell personal data',
        ].map((item) => (
          <div className={styles.Card} key={item}>
            <strong>{item}</strong>
          </div>
        ))}
      </div>
    </MarketingSection>
    <MarketingSection
      id="compliance"
      title="Compliance matrix"
      intro="The frameworks we work to, where we stand on each and the evidence available to your information governance team."
    >
      <div className={styles.TableWrap}>
        <table className={styles.Table}>
          <thead>
            <tr>
              <th>Framework</th>
              <th>Status</th>
              <th>Evidence</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {complianceRows.map(([framework, status, evidence, unverified]) => (
              <tr key={framework}>
                <td>{framework}</td>
                <td>{status}</td>
                <td>{evidence}</td>
                <td>
                  <Link to={`${marketingPaths.contact}?subject=${encodeURIComponent(framework)}`}>Request</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MarketingSection>
    <MarketingSection
      id="principles"
      title="Data processing principles"
      intro="For Trust and university accounts, your organisation is the data controller and Careerbook acts as processor on your documented instructions."
    >
      <div className={styles.Grid3}>
        {[
          [
            'Data minimisation',
            'We process name, email, role, education details and event attendance. Date of birth is optional.',
          ],
          [
            'Location, only at sign-in',
            'We record one position at sign-in when enabled and never track anyone between sessions.',
          ],
          ['No sale, no advertising', 'We never sell personal data or use it for third-party advertising.'],
          ['AI with boundaries', 'AI timetabling and insights use organisation data to serve that organisation.'],
          ['Purpose limitation', 'Data supports teaching, attendance, feedback, certificates and requested reports.'],
          [
            'Deletion on exit',
            'At contract end, data is returned or deleted in line with the data processing agreement.',
          ],
        ].map(([title, body]) => (
          <div className={styles.Card} key={title}>
            <h3>{title}</h3>
            <p>{body}</p>
          </div>
        ))}
      </div>
    </MarketingSection>
  </MarketingPage>
);

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const submitContactForm = async (payload: {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  message: string;
}) => {
  const backendOrigin = getConfigOrThrow('gossamer-be-origin').replace(/\/$/, '');
  const response = await fetch(`${backendOrigin}/email/send-email`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = (await response.json().catch((): null => null)) as { message?: string } | null;
    throw new Error(body?.message || 'Unable to send your message. Please try again.');
  }
};

const ContactForm = () => {
  const params = new URLSearchParams(window.location.search);
  const options = [
    'NHS hospital or Trust',
    'University or medical school',
    'Student society',
    'Course or CPD provider',
  ];
  const requestedType = params.get('type');
  const [organisationType, setOrganisationType] = React.useState(
    options.includes(requestedType || '') ? requestedType || options[0] : options[0],
  );
  const [status, setStatus] = React.useState<FormStatus>('idle');
  const [error, setError] = React.useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    setStatus('submitting');
    setError('');
    const form = new FormData(formElement);
    const firstName = String(form.get('firstName') || '');
    const lastName = String(form.get('lastName') || '');
    const organisation = String(form.get('organisation') || '');
    const subject = params.get('subject');
    const detail = String(form.get('message') || '');
    const message = [
      `Organisation type: ${organisationType}`,
      `Organisation: ${organisation}`,
      subject ? `Enquiry: ${subject}` : '',
      '',
      detail,
    ]
      .filter(Boolean)
      .join('\n');

    try {
      await submitContactForm({
        firstName,
        lastName,
        email: String(form.get('email') || ''),
        phoneNumber: String(form.get('phoneNumber') || ''),
        message,
      });
      setStatus('success');
      formElement.reset();
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : 'Unable to send your message.');
      setStatus('error');
    }
  };

  return (
    <form className={styles.Form} onSubmit={handleSubmit}>
      <fieldset>
        <legend>Organisation type</legend>
        <div className={styles.Pills}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={organisationType === option}
              className={`${styles.Pill} ${organisationType === option ? styles.PillActive : ''}`}
              onClick={() => setOrganisationType(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </fieldset>
      <div className={styles.FormGrid}>
        <label className={styles.Field}>
          First name
          <input name="firstName" required autoComplete="given-name" />
        </label>
        <label className={styles.Field}>
          Last name
          <input name="lastName" required autoComplete="family-name" />
        </label>
        <label className={styles.Field}>
          Work email
          <input name="email" type="email" required autoComplete="email" />
        </label>
        <label className={styles.Field}>
          Phone (optional)
          <input name="phoneNumber" type="tel" autoComplete="tel" />
        </label>
      </div>
      <label className={styles.Field}>
        Organisation
        <input name="organisation" required autoComplete="organization" />
      </label>
      <label className={styles.Field}>
        How can we help?
        <textarea name="message" required defaultValue={params.get('subject') || ''} />
      </label>
      <div>
        <button className={styles.Button} type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Sending…' : 'Send message'}
        </button>
      </div>
      <p className={styles.Status} role="status">
        {status === 'success' && 'Message sent — we’ll reply within 24 hours.'}
        {status === 'error' && error}
      </p>
      <p>
        We’ll only use your details to reply to your enquiry. <Link to={marketingPaths.trust}>Privacy notice</Link>
      </p>
    </form>
  );
};

function DemoRequestForm() {
  const [status, setStatus] = React.useState<FormStatus>('idle');
  const [error, setError] = React.useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const fullName = String(form.get('fullName') || '').trim();
    const nameParts = fullName.split(/\s+/);
    const firstName = nameParts.shift() || fullName;
    const lastName = nameParts.join(' ') || 'Not provided';
    setStatus('submitting');
    setError('');

    try {
      await submitContactForm({
        firstName,
        lastName,
        email: String(form.get('email') || ''),
        message: [
          'Demo request',
          `Role: ${String(form.get('role') || '')}`,
          `NHS Trust or organisation: ${String(form.get('organisation') || '')}`,
          `Number of learners: ${String(form.get('learnerCount') || '')}`,
        ].join('\n'),
      });
      setStatus('success');
      formElement.reset();
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : 'Unable to send your request.');
      setStatus('error');
    }
  };

  return (
    <form className={styles.Form} onSubmit={handleSubmit}>
      <label className={styles.Field}>
        Full name
        <input name="fullName" required autoComplete="name" />
      </label>
      <label className={styles.Field}>
        Role
        <input name="role" required placeholder="e.g. Medical Education Manager" autoComplete="organization-title" />
      </label>
      <label className={styles.Field}>
        Work email
        <input name="email" type="email" required placeholder="name@nhs.net" autoComplete="email" />
      </label>
      <label className={styles.Field}>
        NHS Trust or organisation
        <input name="organisation" required autoComplete="organization" />
      </label>
      <label className={styles.Field}>
        Number of learners
        <select name="learnerCount" required defaultValue="">
          <option value="" disabled>
            Select a range
          </option>
          <option>Under 100</option>
          <option>100–499</option>
          <option>500–999</option>
          <option>1,000–2,499</option>
          <option>2,500+</option>
        </select>
      </label>
      <div>
        <button className={styles.Button} type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Sending…' : 'Book my demo'}
        </button>
      </div>
      <p className={styles.Status} role="status">
        {status === 'success' && 'Thanks — we’ll be in touch within 24 hours.'}
        {status === 'error' && error}
      </p>
      <p>
        We’ll use your details only to arrange your demo. <Link to={marketingPaths.trust}>See our privacy notice.</Link>
      </p>
    </form>
  );
}

export const ContactPage = () => (
  <MarketingPage>
    <MarketingHero
      eyebrow="Contact Careerbook"
      title="Let’s talk about your teaching"
      summary="Book a demo, ask about pricing or get help with your account. Our team responds to every enquiry within 24 hours."
    />
    <MarketingSection>
      <div className={styles.Grid3}>
        <a className={styles.Card} href="mailto:info@careerbook.co.uk">
          <h3>Email</h3>
          <p>info@careerbook.co.uk</p>
        </a>
        <a className={styles.Card} href="tel:+447388580297">
          <h3>Phone</h3>
          <p>+44 (0)7388 580 297</p>
        </a>
        <Link className={styles.Card} to={externalPaths.videoGuides}>
          <h3>Already a customer?</h3>
          <p>Visit the video guides and help centre.</p>
        </Link>
      </div>
    </MarketingSection>
    <MarketingSection title="Send us a message" narrow>
      <ContactForm />
    </MarketingSection>
  </MarketingPage>
);

export const MarketingFeaturesIndex = () => (
  <MarketingPage>
    <MarketingHero
      eyebrow="Platform"
      title="Every teaching workflow, connected."
      summary="Explore the Careerbook tools that take a session from timetable to attendance, feedback, certificate and audit report."
    />
    <MarketingSection>
      <FeatureLinks />
    </MarketingSection>
  </MarketingPage>
);
